import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  DraggableDialog,
  DraggableDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/draggable-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Combobox,
  ComboboxChips,
  ComboboxValue,
  ComboboxInput,
  ComboboxPopup,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
  ComboboxChip,
} from '@/components/ui/combobox';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useAnnouncer } from '@/hooks/useAnnouncer';
import { useTags } from '@/hooks/useTags';
import type { WorkflowMetadata } from '@/hooks/useWorkflow';
import { FooterActions } from './WorkflowSaveDialogFooterActions';

interface WorkflowSaveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (metadata: WorkflowMetadata) => Promise<void>;
  initialData?: Partial<WorkflowMetadata>;
  mode: 'create' | 'update';
}

export function WorkflowSaveDialog({
  open,
  onOpenChange,
  onSave,
  initialData,
  mode,
}: WorkflowSaveDialogProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'draft' | 'active' | 'archived'>('draft');
  const [visibility, setVisibility] = useState<'private' | 'team' | 'organization'>('private');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Tags data for combobox
  const { tags: availableTags } = useTags({});
  const tagItems = useMemo(() => availableTags.map(t => ({ label: t.name, value: t.name })), [availableTags]);
  const selectedTagItems = useMemo(() => tags.map(t => ({ label: t, value: t })), [tags]);

  // Focus management
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Track if form is dirty
  const isDirty = useMemo(() => {
    if (!initialData) {
      return name !== '' || code !== '' || description !== '' || tags.length > 0;
    }
    // PROJ-010 ISSUE-003: Compare tag names, not objects
    const initialTagNames = Array.isArray(initialData.tags) && initialData.tags.length > 0 && typeof initialData.tags[0] === 'object'
      ? initialData.tags.map((t: any) => t.name)
      : (initialData.tags || []);
    return (
      name !== (initialData.name || '') ||
      code !== (initialData.code || '') ||
      description !== (initialData.description || '') ||
      status !== (initialData.status || 'draft') ||
      visibility !== (initialData.visibility || 'private') ||
      tags.length !== initialTagNames.length || !tags.every(t => initialTagNames.includes(t))
    );
  }, [name, code, description, status, visibility, tags, initialData]);

  // useUnsavedChanges hook
  const { handleClose: handleUnsavedClose, UnsavedChangesDialog } = useUnsavedChanges({
    isDirty,
    onClose: () => onOpenChange(false),
  });

  // useAnnouncer for accessibility
  const { announce } = useAnnouncer();

  // Reset form when dialog opens or initial data changes
  useEffect(() => {
    if (open && initialData) {
      setName(initialData.name || '');
      setCode(initialData.code || '');
      setDescription(initialData.description || '');
      setStatus(initialData.status || 'draft');
      setVisibility(initialData.visibility || 'private');
      // PROJ-010 ISSUE-003: Handle tags as array of objects or strings
      const tagsData = initialData.tags || [];
      const tagNames = Array.isArray(tagsData) && tagsData.length > 0 && typeof tagsData[0] === 'object'
        ? tagsData.map((t: any) => t.name)
        : tagsData;
      setTags(tagNames);
    } else if (open && !initialData) {
      // Reset for new workflow
      setName('');
      setCode('');
      setDescription('');
      setStatus('draft');
      setVisibility('private');
      setTags([]);
    }
  }, [open, initialData]);

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      announce('El nombre del flujo visual es obligatorio', 'assertive');
      return;
    }

    setIsSaving(true);
    try {
      // Map selected tag names to IDs expected by backend (workflow_tags)
      const tagNameSet = new Set(tags);
      const tagIds = availableTags.filter(t => tagNameSet.has(t.name)).map(t => t.id)

      const metadata: WorkflowMetadata = {
        name: name.trim(),
        description: description.trim() || undefined,
        status,
        visibility,
        tags,
      } as any;
      (metadata as any).tagIds = tagIds;

      // Include code if provided (backend will generate if not)
      if (mode === 'create' && code.trim()) {
        metadata.code = code.trim();
      }

      await onSave(metadata);
      announce(mode === 'create' ? 'Flujo visual creado exitosamente' : 'Flujo visual actualizado exitosamente', 'polite');
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving workflow:', error);
      announce('Error al guardar el flujo visual', 'assertive');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-focus on first input when opening
  useEffect(() => {
    if (open && firstInputRef.current) {
      const timer = setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Escape - Close with confirmation if there are changes
      if (e.key === 'Escape') {
        e.preventDefault();
        handleUnsavedClose();
      }
      // Ctrl+S or Cmd+S - Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (name.trim() && !isSaving) {
          handleSave();
        }
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, name, isSaving, handleUnsavedClose]);

  return (
    <DraggableDialog open={open} onOpenChange={(next) => !next && handleUnsavedClose()}>
      <DraggableDialogContent className="flex flex-col sm:max-w-[525px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Crear Nuevo Flujo Visual' : 'Guardar Cambios'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Configura los detalles para tu nuevo flujo visual.'
              : 'Actualiza la configuración del flujo visual.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="grid gap-4 pb-4">
          {/* Name */}
          <div className="grid gap-2">
            <Label htmlFor="name">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mi flujo visual de datos"
              required
              ref={firstInputRef}
            />
          </div>

          {/* Code (only for create) */}
          {mode === 'create' && (
            <div className="grid gap-2">
              <Label htmlFor="code">
                Código
                <span className="text-muted-foreground text-xs ml-2">(opcional, se generará automáticamente)</span>
              </Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="mi-workflow-001"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Identificador único en formato kebab-case (ej: mi-workflow-datos)
              </p>
            </div>
          )}

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción del workflow..."
              rows={3}
            />
          </div>

          {/* Status */}
          <div className="grid gap-2">
            <Label htmlFor="status">Estado</Label>
            <Select value={status} onValueChange={(value: any) => setStatus(value)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="archived">Archivado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Visibility */}
          <div className="grid gap-2">
            <Label htmlFor="visibility">Visibilidad</Label>
            <Select value={visibility} onValueChange={(value: any) => setVisibility(value)}>
              <SelectTrigger id="visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Privado</SelectItem>
                <SelectItem value="team">Equipo</SelectItem>
                <SelectItem value="organization">Organización</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tags */}
          <div className="grid gap-2">
            <Label htmlFor="tags">Tags</Label>
            <Combobox
              items={tagItems}
              multiple
              value={selectedTagItems}
              onValueChange={(val) => setTags(Array.isArray(val) ? val.map(v => v.label) : val ? [val.label] : [])}
            >
              <ComboboxChips>
                <ComboboxValue>
                  {(value: any) => (
                    <>
                      {Array.isArray(value) && value.map((item: any) => (
                        <ComboboxChip key={item.value} aria-label={item.label}>
                          {item.label}
                        </ComboboxChip>
                      ))}
                      <ComboboxInput aria-label="Seleccionar tags" placeholder={(Array.isArray(value) && value.length > 0) ? undefined : 'Seleccionar tags…'} />
                    </>
                  )}
                </ComboboxValue>
              </ComboboxChips>
              <ComboboxPopup className="z-[var(--z-dialog-dragging)]">
                <ComboboxEmpty>No se encontraron tags.</ComboboxEmpty>
                <ComboboxList>
                  {(item) => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxPopup>
            </Combobox>
          </div>
          </div>
        </ScrollArea>

        {/* Action Buttons - Fixed Footer */}
        <FooterActions
          isSubmitting={isSaving}
          handleSave={handleSave}
          requestClose={handleUnsavedClose}
          isNameValid={!!name.trim()}
          mode={mode}
        />

        {/* Unsaved Changes Dialog */}
        {UnsavedChangesDialog}
      </DraggableDialogContent>
    </DraggableDialog>
  );
}
